import axios from 'axios'
import { extractParams, updateHistory } from 'src/utils/utils'

const CancelToken = axios.CancelToken

class AuthApi {
    constructor(apiToken) {
        this.apiToken = apiToken.startsWith('Bearer ')
            ? apiToken
            : `Bearer ${apiToken}`
        this.client = axios.create({
            baseURL: '/api/auth',
            timeout: 20000,
            headers: { Authorization: this.apiToken },
        })
        this.source = CancelToken.source()
    }

    /**
     * @param page
     * @param history
     * @param params
     * @param organizationId
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchUsers(
        page = null,
        history = null,
        params = {},
        organizationId = null
    ) {
        let { urlSearchParams, currentPage, location } = extractParams(
            page,
            history
        )

        let url = '/users'
        if (organizationId) {
            url = `/tenants/${organizationId}/users`
        }

        let promise = this.client.get(url, {
            cancelToken: this.source.token,
            params: {
                per_page: 20,
                page: currentPage,
                load_relationships: true,
                ...params,
            },
        })

        promise
            .then(() => {
                updateHistory(history, location, currentPage, urlSearchParams)
            })
            .catch(err => console.error(err))

        return promise
    }

    cancelRequest() {
        this.source.cancel('Operation canceled by the user.')
    }

    /**
     * @param userId
     * @param groupId
     * @returns {Promise<AxiosResponse<T>>}
     */
    addUserToGroup(userId, groupId) {
        return this.client.post(`/groups/${groupId}/users`, {
            ids: [userId],
        })
    }

    /**
     * @param userId
     * @param tenantId
     * @returns {Promise<AxiosResponse<T>>}
     */
    addUserToTenant(userId, tenantId) {
        return this.client.post(`/tenants/${tenantId}/users`, {
            ids: [userId],
        })
    }

    /**
     * @param userId
     * @param groupId
     * @returns {Promise<AxiosResponse<T>>}
     */
    removeUserFromGroup(userId, groupId) {
        return this.client.delete(`/groups/${groupId}/users/${userId}`)
    }

    fetchUser(username) {
        return this.client.get(`/users/${username}`, {
            cancelToken: this.source.token,
        })
    }

    fetchTenantGroups(tenant_id) {
        return this.client.get('/tenants/' + tenant_id + '/groups', {
            params: {
                per_page: 1000,
            },
        })
    }

    /**
     * @param username
     * @returns {Promise<AxiosResponse<T>>}
     */
    deleteUser(username) {
        return this.client.delete(`/users/${username}`)
    }

    /**
     * @param page
     * @param history
     * @param params
     * @param organizationId
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchGroups(
        page = null,
        history = null,
        params = {},
        organizationId = null
    ) {
        let { urlSearchParams, currentPage, location } = extractParams(
            page,
            history
        )

        let url = '/groups'
        if (organizationId) {
            url = `/tenants/${organizationId}/groups`
        }

        let promise = this.client.get(url, {
            cancelToken: this.source.token,
            params: {
                per_page: 20,
                page: currentPage,
                load_relationships: true,
                ...params,
            },
        })

        promise
            .then(() => {
                updateHistory(history, location, currentPage, urlSearchParams)
            })
            .catch(err => console.error(err))

        return promise
    }

    fetchGroupsCounted(params, organizationId = null) {
        let url = '/groups/count'
        if (organizationId) {
            url = `/tenants/${organizationId}/groups/count`
        }
        return this.client.get(url, {
            cancelToken: this.source.token,
            params: {
                ...params,
            },
        })
    }

    fetchGroup(groupId) {
        return this.client.get(`/groups/${groupId}`, {
            cancelToken: this.source.token,
        })
    }

    /**
     * @param groupId
     * @returns {Promise<AxiosResponse<T>>}
     */
    deleteGroup(groupId) {
        return this.client.delete(`/groups/${groupId}`)
    }

    /**
     * @returns {Promise<AxiosResponse<any>>}
     */
    fetchDefaultGroups() {
        return this.client.get('/default-groups', {
            cancelToken: this.source.token,
        })
    }

    /**
     * @param payload
     * @returns {Promise<AxiosResponse<any>>}
     */
    createDefaultGroups(payload) {
        return this.client.post('/default-groups', payload)
    }

    /**
     * @param userId
     * @param tenantId
     * @returns {Promise<AxiosResponse<T>>}
     */
    removeUserFromTenant(userId, tenantId) {
        return this.client.delete(`/tenants/${tenantId}/users/${userId}`)
    }

    /**
     * @param userId
     * @param tenantId
     * @returns {Promise<AxiosResponse<T>>}
     */
    removeUserAsTenantAdmin(userId, tenantId) {
        return this.client.put(`/tenants/${tenantId}/users/${userId}`, {
            is_admin: false,
        })
    }

    /**
     * @param childTenantId
     * @returns {Promise<AxiosResponse<T>>}
     */
    removeTenantAsChildTenant(childTenantId) {
        return this.client.put(`/tenants/${childTenantId}`, {
            parent_tenant_id: null,
        })
    }

    /**
     * @param params
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchSuppliers(params = {}) {
        return this.client.get('/suppliers', {
            cancelToken: this.source.token,
            params: {
                per_page: 20,
                page: 1,
                load_relationships: true,
                ...params,
            },
        })
    }

    /**
     * @param page
     * @param history
     * @param params
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchTenants(page = null, history = null, params = {}) {
        let { urlSearchParams, currentPage, location } = extractParams(
            page,
            history
        )

        let promise = this.client.get('/tenants', {
            cancelToken: this.source.token,
            params: {
                per_page: 20,
                page: currentPage,
                load_relationships: true,
                ...params,
            },
        })

        promise
            .then(() => {
                updateHistory(history, location, currentPage, urlSearchParams)
            })
            .catch(err => console.error(err))

        return promise
    }

    fetchTenant(tenantId) {
        return this.client.get(`/tenants/${tenantId}`, {
            cancelToken: this.source.token,
        })
    }

    /**
     * @param tenantId
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchChildTenants(tenantId) {
        return this.client.get(`/tenants/${tenantId}/subtenants`, {
            cancelToken: this.source.token,
        })
    }

    fetchCompanies() {
        return this.client.get('/companies', {
            cancelToken: this.source.token,
            params: {
                per_page: 1000,
            },
        })
    }

    /**
     * @param page
     * @param history
     * @param params
     * @param organizationId
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchServices(
        page = null,
        history = null,
        params = {},
        organizationId = null
    ) {
        let { urlSearchParams, currentPage, location } = extractParams(
            page,
            history
        )

        let promise = this.client.get('/services', {
            cancelToken: this.source.token,
            params: {
                per_page: 20,
                page: currentPage,
                ...params,
            },
        })

        promise
            .then(() => {
                updateHistory(history, location, currentPage, urlSearchParams)
            })
            .catch(err => console.error(err))

        return promise
    }

    deleteService(serviceId) {
        return this.client.delete(`/services/${serviceId}`)
    }

    fetchApplicationServiceActions(
        applicationId,
        page = 1,
        per_page = 10,
        params = {}
    ) {
        return this.client.get(`/applications/${applicationId}/actions`, {
            cancelToken: this.source.token,
            params: {
                page: page,
                per_page: per_page,
                ...params,
            },
        })
    }

    deleteApplicationServiceAction(applicationId, serviceActionId) {
        return this.client.delete(
            `/applications/${applicationId}/actions/${serviceActionId}`
        )
    }

    fetchService(serviceId) {
        return this.client.get(`/services/${serviceId}`, {
            cancelToken: this.source.token,
        })
    }

    fetchServiceActions(serviceId, page = 1, per_page = 10, params = {}) {
        return this.client.get(`/services/${serviceId}/actions`, {
            cancelToken: this.source.token,
            params: {
                page: page,
                per_page: per_page,
                sort: '+category',
                ...params,
            },
        })
    }

    fetchServiceAction(serviceId, serviceActionId) {
        return this.client.get(
            `/services/${serviceId}/actions/${serviceActionId}`,
            { cancelToken: this.source.token }
        )
    }

    /**
     * @param page
     * @param history
     * @param params
     * @param organizationId
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchUserSessions(
        page = null,
        history = null,
        params = {},
        organizationId = null
    ) {
        const { urlSearchParams, currentPage, location } = extractParams(
            page,
            history
        )

        const url = '/user_sessions'

        let promise = this.client.get(url, {
            cancelToken: this.source.token,
            params: {
                per_page: 20,
                page: currentPage,
                ...params,
            },
        })

        promise
            .then(() => {
                updateHistory(history, location, currentPage, urlSearchParams)
            })
            .catch(err => console.error(err))

        return promise
    }

    deleteServiceAction(serviceId, serviceActionId) {
        return this.client.delete(
            `/services/${serviceId}/actions/${serviceActionId}`
        )
    }

    setUserAsTenantAdmin(userId, tenantId) {
        return this.client.put(`/tenants/${tenantId}/users/${userId}`, {
            is_admin: true,
        })
    }

    /**
     * @param childTenantId
     * @param tenantId
     * @returns {Promise<AxiosResponse<T>>}
     */
    setTenantAsChildTenant(childTenantId, tenantId) {
        return this.client.put(`/tenants/${childTenantId}`, {
            parent_tenant_id: tenantId,
        })
    }

    /**
     * @param page
     * @param history
     * @param params
     * @param group_id
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchGroupAppPermissions(
        page = null,
        history = null,
        params = {},
        group_id
    ) {
        let { urlSearchParams, currentPage, location } = extractParams(
            page,
            history
        )

        let promise = this.client.get(`/groups/${group_id}/app_permissions`, {
            cancelToken: this.source.token,
            params: {
                per_page: 20,
                page: currentPage,
                load_relationships: true,
                ...params,
            },
        })

        promise.then(() => {
            updateHistory(history, location, currentPage, urlSearchParams)
        })

        return promise
    }

    /**
     * @param page
     * @param group_id
     * @param permissionId
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchAppPermissions(page = null, group_id, permissionId) {
        let { currentPage } = extractParams(page)

        let promise = this.client.get(
            `/groups/${group_id}/app_permissions/${permissionId}`,
            {
                cancelToken: this.source.token,
                params: {
                    per_page: 10,
                    page: currentPage,
                },
            }
        )

        return promise
    }

    /**
     * @param page
     * @param applicationId
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchApplicationActions(applicationId, page = 1, params = {}) {
        return this.client.get(`/applications/${applicationId}/actions`, {
            cancelToken: this.source.token,
            params: {
                page: page,
                per_page: 10,
                ...params,
            },
        })
    }

    /**
     * @param page
     * @param history
     * @param params
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchApplications(page = null, history = null, params = {}) {
        let { urlSearchParams, currentPage, location } = extractParams(
            page,
            history
        )

        let promise = this.client.get('/applications', {
            cancelToken: this.source.token,
            params: {
                per_page: 20,
                page: currentPage,
                ...params,
            },
        })
        promise
            .then(() => {
                updateHistory(history, location, currentPage, urlSearchParams)
            })
            .catch(err => console.error(err))

        return promise
    }

    fetchApplication(applicationId) {
        return this.client.get(`/applications/${applicationId}`, {
            cancelToken: this.source.token,
        })
    }

    deleteApplication(applicationId) {
        return this.client.delete('/applications/' + applicationId)
    }

    fetchAppPermission(groupId, permissionId) {
        return this.client.get(
            `/groups/${groupId}/app_permissions/${permissionId}`,
            { cancelToken: this.source.token }
        )
    }

    removeAppPermission(groupId, permissionId) {
        return this.client.delete(
            `/groups/${groupId}/app_permissions/${permissionId}`
        )
    }

    deleteTenant(tenantId) {
        return this.client.delete('/tenants/' + tenantId)
    }

    deleteUserSession(userSessionId) {
        return this.client.delete(`/user_sessions/${userSessionId}`)
    }
}

export { AuthApi }
